<script setup lang="ts">
import { watch } from 'vue'
import Button from 'primevue/button'
import { Icon } from '@iconify/vue'
import { userRoleOptions, type UserRole} from '@/constants/user'
import FormInputText from '@/components/inputs/FormInputText.vue'
import FormDropdown from '@/components/inputs/FormDropdown.vue'
import { useUserForm } from '@/composables/useUserForm'
import type { User, Users } from '@/types/user-types'
import { editUser } from '@/api/users'

const props = defineProps<{
  userDataToEdit: Users
}>()

const emit = defineEmits<{
  (e: 'userEdited'): void;
  (e: 'submissionFailed', errorDetail?: string): void;
  (e: 'cancel'): void;
}>()

const {
  values,
  errors,
  meta,
  handleSubmit,
  loading,
  showToast,
  resetForm
} = useUserForm('edit')

watch(() => props.userDataToEdit, (newUserData) => {
  if (newUserData) {
    resetForm({
      values: {
        firstName: newUserData.firstName,
        lastName: newUserData.lastName,
        email: newUserData.email,
        userRole: newUserData.userRole,
      },
    });
  } else {
    resetForm();
  }
}, { immediate: true });

const performUserUpdate = async (userDataPayload: User): Promise<boolean> => {
  loading.value = true
  if (!props.userDataToEdit?.id) {
    showToast('error', 'Error', 'User ID is missing. Cannot update user.')
    loading.value = false
    return false
  }
  try {
    await editUser(props.userDataToEdit.id.toString(), userDataPayload)
    showToast('success', 'Success', `User ${userDataPayload.firstName} ${userDataPayload.lastName} has been updated successfully`)
    return true
  } catch (error) {
    console.error('Failed to update user:', error)
    const errorResponseMessage = (error as any)?.response?.data?.message || 'Failed to update user. Please try again.';
    showToast('error', 'Error', errorResponseMessage)
    return false
  } finally {
    loading.value = false
  }
}

const onSubmit = handleSubmit(async (formValues) => {
  let apiCompatibleUserRole: UserRole;
  const selectedRoleOption = userRoleOptions.find(option => option.value === formValues.userRole);

  if (selectedRoleOption) {
    apiCompatibleUserRole = formValues.userRole;
  } else {
    showToast('error', 'Error', 'Selected user role is invalid or missing.');
    emit('submissionFailed', 'Invalid user role selected.');
    return;
  }

  const userDataForApi: User = {
    firstName: formValues.firstName,
    lastName: formValues.lastName,
    email: formValues.email,
    userRole: apiCompatibleUserRole,
  };

  const success = await performUserUpdate(userDataForApi);

  if (success) {
    emit('userEdited');
  }
})

const handleCancel = () => {
  emit('cancel')
}
</script>

<template>
  <form @submit.prevent="onSubmit" class="p-8 bg-white rounded-xl shadow-md">
    <div class="text-center mb-12">
      <h2 class="text-2xl font-semibold mb-4">Editing User</h2>
      <p class="text-gray-600 mt-2">
        Modify the user's details below.
      </p>
      <div class="border-t border-gray-200 my-6"></div>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 mb-4 gap-8">
      <div class="flex flex-col">
        <label class="text-gray-700 mb-2">First Name</label>
        <FormInputText
          inputName="firstName"
          id="editFirstName"
          label=""
          v-model="values.firstName"
          :errorMessage="errors.firstName"
          placeholder="Enter user's first name"
          class="w-full"
        />
      </div>

      <div class="flex flex-col">
        <label class="text-gray-700 mb-2">Last Name</label>
        <FormInputText
          inputName="lastName"
          id="editLastName"
          label=""
          v-model="values.lastName"
          :errorMessage="errors.lastName"
          placeholder="Enter user's surname"
          class="w-full"
        />
      </div>

      <div class="flex flex-col">
        <label class="text-gray-700 mb-2">Email Address</label>
        <FormInputText
          inputName="email"
          id="editEmail"
          label=""
          v-model="values.email"
          :errorMessage="errors.email"
          placeholder="Enter user's email address here"
          class="w-full"
        />
      </div>

      <div class="flex flex-col">
        <label class="text-gray-700 mb-2">User Role</label>
        <FormDropdown
          inputName="userRole"
          id="editUserRole"
          v-model="values.userRole"
          :options="userRoleOptions"
          optionLabel="label"
          optionValue="value"
          :errorMessage="errors.userRole"
          placeholder="Choose user role"
          class="w-full"
        />
      </div>
    </div>
    <div class="flex justify-end gap-6 mt-10">
      <Button
        type="button"
        outlined
        class="px-8 py-2.5 border border-gray-300 text-gray-700"
        @click="handleCancel"
        :disabled="loading"
      >
        Cancel
      </Button>

      <Button
        v-ripple
        type="submit"
        class="px-8 py-2.5 flex items-center justify-center bg-orange-500 hover:bg-orange-600 text-white"
        :disabled="!meta.valid || loading"
        :loading="loading"
      >
        <span>Save Changes</span>
        <Icon v-if="!loading" icon="mdi:check-circle" class="text-xl ml-2" />
      </Button>
    </div>
  </form>
</template>

<style scoped>
:deep(.p-inputtext) {
  padding: 0.75rem 1rem;
}

:deep(.p-dropdown) {
  width: 100%;
}
</style>